<template>
  <div :class="['bpmn-node', nodeClass]">
    <!-- Сверху: один хэндл, работает и как source, и как target (loose mode) -->
    <Handle type="source" :position="Position.Top"    id="top" />

    <div class="bpmn-node__icon">{{ nodeIcon }}</div>

    <div class="bpmn-node__body">
      <div class="bpmn-node__label">
        {{ data.node_label }}
        <span class="bpmn-node__id">#{{ data.bpmn_node_id }}</span>
      </div>

      <!-- Роль: код + русское название -->
      <div v-if="data.assign_type_code" class="bpmn-node__role">
        <span class="bpmn-node__badge bpmn-node__badge--role">{{ data.assign_type_code }}</span>
        <span class="bpmn-node__badge-ru">{{ roleLabel }}</span>
      </div>

      <!-- Задача: код + русское название -->
      <div v-if="data.task_type_code" class="bpmn-node__task">
        <span class="bpmn-node__badge bpmn-node__badge--task">{{ data.task_type_code }}</span>
        <span class="bpmn-node__badge-ru">{{ taskLabel }}</span>
      </div>

      <!-- Авто-действие -->
      <div v-if="data.auto_action_code" class="bpmn-node__auto">
        ⚡ <span class="bpmn-node__badge bpmn-node__badge--auto">{{ data.auto_action_code }}</span>
      </div>

      <!-- AND-join индикатор -->
      <div v-if="data.join_type === 'and'" class="bpmn-node__join-and" title="AND-join: ждёт все входящие ветки">
        ⊕ AND
      </div>
    </div>

    <div v-if="data.isCurrent" class="bpmn-node__current-badge">📍 Здесь</div>
    <div v-if="data.isCompleted" class="bpmn-node__done-badge">✓ Готово</div>

    <!-- Данные из instance node -->
    <div v-if="data.instanceNode" class="bpmn-node__instance-info">
      <span v-if="data.instanceNode.employee_fio" class="bpmn-node__employee">
        👤 {{ data.instanceNode.employee_fio }}
      </span>
      <div class="bpmn-node__instance-meta">
        <span v-if="data.instanceNode.employee_task_id" class="bpmn-node__task-link">
          #{{ data.instanceNode.employee_task_id }}
        </span>
        <span v-if="data.instanceNode.completed_at" class="bpmn-node__completed-at">
          {{ formatDate(data.instanceNode.completed_at) }}
        </span>
      </div>
      <!-- Ошибка -->
      <div v-if="data.instanceNode.last_error" class="bpmn-node__error">
        <div class="bpmn-node__error-text">⚠ {{ data.instanceNode.last_error }}</div>
        <div v-if="data.instanceNode.error_at" class="bpmn-node__error-at">
          {{ formatDate(data.instanceNode.error_at) }}
        </div>
      </div>
      <!-- Кнопка ручного запуска для активных узлов (только не в режиме просмотра) -->
      <button
        v-if="data.instanceNode.status === 'active' && !data.readonly"
        class="bpmn-node__fire-btn"
        @click.stop="data.onFireEvent && data.onFireEvent(data.instanceNode)"
      >
        ▶ Запустить событие
      </button>
    </div>

    <!-- Лево, право, низ — все source, loose mode позволяет цеплять к любому -->
    <Handle type="source" :position="Position.Left"   id="left" />
    <Handle type="source" :position="Position.Right"  id="right" />
    <Handle type="source" :position="Position.Bottom" id="bottom" />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { Handle, Position } from '@vue-flow/core'
import { getRoleLabel, getTaskLabel } from '../bpmnDicts'

const props = defineProps({
  data: { type: Object, required: true },
})


const nodeClass = computed(() => {
  const t = props.data.node_type
  if (props.data.isCompleted) return `bpmn-node--${t} bpmn-node--completed`
  if (t === 'start') return 'bpmn-node--start'
  if (t === 'end')   return 'bpmn-node--end'
  if (t === 'auto')  return props.data.isCurrent ? 'bpmn-node--auto bpmn-node--current' : 'bpmn-node--auto'
  if (t === 'event') return 'bpmn-node--event'
  if (t === 'task')  return props.data.isCurrent ? 'bpmn-node--task bpmn-node--current' : 'bpmn-node--task'
  return ''
})

const nodeIcon = computed(() => {
  const t = props.data.node_type
  if (t === 'start') return '▶'
  if (t === 'end')   return '⏹'
  if (t === 'auto')  return '⚙'
  if (t === 'event') return '◆'
  return '☐'
})

const roleLabel = computed(() => getRoleLabel(props.data.assign_type_code))
const taskLabel = computed(() => getTaskLabel(props.data.task_type_code))

function formatDate(dt) {
  if (!dt) return ''
  return new Date(dt).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}
</script>

<style scoped>
/* Хэндлы */
:deep(.vue-flow__handle) {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2.5px solid #1976d2;
  background: #fff;
  cursor: crosshair;
  transition: background 0.15s, transform 0.15s, box-shadow 0.15s;
  z-index: 10;
}
:deep(.vue-flow__handle:hover) {
  background: #1976d2;
  transform: scale(1.5);
  box-shadow: 0 0 0 4px rgba(25, 118, 210, 0.2);
}
:deep(.vue-flow__handle-connecting) {
  background: #43a047;
  border-color: #43a047;
  transform: scale(1.6);
  box-shadow: 0 0 0 5px rgba(67, 160, 71, 0.25);
}
:deep(.vue-flow__handle-valid) {
  background: #43a047;
  border-color: #43a047;
}
:deep(.vue-flow__handle-right)  { right: -8px; }
:deep(.vue-flow__handle-left)   { left: -8px; }
:deep(.vue-flow__handle-top)    { top: -8px; }
:deep(.vue-flow__handle-bottom) { bottom: -8px; }

.bpmn-node {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 2px solid #ccc;
  background: #fff;
  min-width: 170px;
  max-width: 240px;
  font-size: 12px;
  position: relative;
  box-shadow: 0 2px 6px rgba(0,0,0,0.12);
}

.bpmn-node--start,
.bpmn-node--end   { border-color: #9e9e9e; background: #f5f5f5; }
.bpmn-node--task  { border-color: #1976d2; background: #e3f2fd; }
.bpmn-node--current {
  border-color: #f57c00;
  background: #fff3e0;
  box-shadow: 0 0 0 3px rgba(245,124,0,0.35);
}
.bpmn-node--auto  { border-color: #e65100; background: #fbe9e7; }
.bpmn-node--event { border-color: #6a1b9a; background: #f3e5f5; }
.bpmn-node--completed {
  border-color: #2e7d32 !important;
  background: #e8f5e9 !important;
  box-shadow: 0 0 0 2px rgba(46,125,50,0.25) !important;
  opacity: 0.85;
}

.bpmn-node__icon {
  font-size: 14px;
  flex-shrink: 0;
  margin-top: 2px;
}

.bpmn-node__body {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.bpmn-node__label {
  font-weight: 600;
  color: #212121;
  line-height: 1.3;
  word-break: break-word;
  display: flex;
  align-items: baseline;
  gap: 5px;
  flex-wrap: wrap;
}

.bpmn-node__id {
  font-size: 11px;
  font-weight: 400;
  color: #9e9e9e;
  flex-shrink: 0;
}

.bpmn-node__role,
.bpmn-node__task,
.bpmn-node__auto {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.bpmn-node__badge {
  font-size: 10px;
  padding: 1px 5px;
  border-radius: 4px;
  font-weight: 600;
  white-space: nowrap;
}

.bpmn-node__badge--role {
  background: rgba(25, 118, 210, 0.12);
  color: #1565c0;
}

.bpmn-node__badge--task {
  background: rgba(56, 142, 60, 0.12);
  color: #2e7d32;
}

.bpmn-node__badge--auto {
  background: rgba(230, 81, 0, 0.12);
  color: #bf360c;
}

.bpmn-node__badge-ru {
  font-size: 10px;
  color: #757575;
  font-style: italic;
}

.bpmn-node__auto {
  font-size: 10px;
  color: #bf360c;
}

.bpmn-node__current-badge {
  position: absolute;
  top: -11px;
  right: 6px;
  background: #f57c00;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
}

.bpmn-node__done-badge {
  position: absolute;
  top: -11px;
  right: 6px;
  background: #2e7d32;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
}

.bpmn-node__instance-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 5px;
  padding-top: 5px;
  border-top: 1px dashed #b0bec5;
}

.bpmn-node__employee {
  font-size: 11px;
  color: #212121;
  font-weight: 600;
  line-height: 1.3;
}

.bpmn-node__instance-meta {
  display: flex;
  align-items: center;
  gap: 6px;
}

.bpmn-node__task-link {
  font-size: 10px;
  color: #1565c0;
  font-weight: 600;
  background: rgba(25,118,210,0.08);
  padding: 1px 5px;
  border-radius: 4px;
}

.bpmn-node__completed-at {
  font-size: 10px;
  color: #757575;
}

.bpmn-node__error {
  margin-top: 4px;
  padding: 4px 6px;
  background: #ffebee;
  border: 1px solid #ef9a9a;
  border-radius: 4px;
}

.bpmn-node__error-text {
  font-size: 10px;
  color: #c62828;
  font-weight: 600;
  word-break: break-word;
  line-height: 1.3;
}

.bpmn-node__error-at {
  font-size: 9px;
  color: #9e9e9e;
  margin-top: 2px;
}

.bpmn-node__join-and {
  margin-top: 4px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  font-weight: 700;
  color: #6a1b9a;
  background: #f3e5f5;
  border: 1px solid #ce93d8;
  border-radius: 4px;
  padding: 2px 6px;
}

.bpmn-node__fire-btn {
  margin-top: 6px;
  width: 100%;
  padding: 4px 8px;
  background: #e3f2fd;
  border: 1px solid #90caf9;
  border-radius: 4px;
  cursor: pointer;
  font-size: 10px;
  font-weight: 600;
  color: #1565c0;
  text-align: center;
  transition: background 0.15s;
}
.bpmn-node__fire-btn:hover {
  background: #bbdefb;
}
</style>
